<script setup lang="ts" generic="TValue extends undefined | true | string">
import { computed, useId, useTemplateRef } from "vue";
import type { InputProps } from "./types";
import useValidation from "./useValidation";

type TModelValue = undefined extends TValue
  ? boolean | null
  : true extends TValue
    ? boolean | null
    : string[];

const modelValue = defineModel<TModelValue>({ required: true });

const props = defineProps<
  InputProps & {
    value?: TValue;
    isSwitch?: boolean;
  }
>();

defineSlots<{
  helpText(): void;
}>();

const elementId = `input-${useId()}`;
const input = useTemplateRef("input");

const { expose, isTouched, error } = useValidation({
  input,
  modelValue,
  props,
});
defineExpose(expose);

const isChecked = computed(() => {
  if (Array.isArray(modelValue.value)) {
    return modelValue.value.includes(props.value as string);
  } else {
    return modelValue.value === (props.value ?? true);
  }
});
</script>

<template>
  <ClientOnly>
    <div
      class="root"
      :class="{ 'is-switch': props.isSwitch, 'is-touched': isTouched }"
    >
      <label v-if="!props.isSwitch" class="checkbox-wrapper" :for="elementId">
        <input
          :id="elementId"
          ref="input"
          type="checkbox"
          :name="props.name"
          :value="props.value ?? true"
          v-model="modelValue"
          class="visually-hidden"
          :disabled="props.isDisabled"
          :readonly="props.isReadonly"
        />
        <div
          class="checkbox-control typeface-regular"
          :class="{
            'is-checked': isChecked,
            'is-indeterminate': modelValue === null,
            'is-disabled': props.isDisabled,
          }"
        >
          <i v-if="isChecked" class="ri-checkbox-line" />
          <i v-if="modelValue === null" class="ri-checkbox-fill" />
        </div>
        <span v-if="props.label" class="control-label typeface-regular">
          <span>{{ props.label }}</span>
        </span>
      </label>

      <label v-else class="switch-wrapper" :for="elementId">
        <input
          :id="elementId"
          ref="input"
          :name="props.name"
          :value="props.value ?? true"
          v-model="modelValue"
          type="checkbox"
          class="visually-hidden"
          :disabled="props.isDisabled"
          :readonly="props.isReadonly"
        />
        <div
          class="switch-control"
          :class="{ 'is-checked': isChecked, 'is-disabled': props.isDisabled }"
        >
          <div class="switch-thumb" />
        </div>
        <span v-if="props.label" class="control-label">
          <span>{{ props.label }}</span>
        </span>
      </label>

      <div class="meta-wrapper">
        <small v-if="$slots.helpText" class="helper-text typeface-regular"
          ><slot name="helpText"></slot
        ></small>
      </div>
    </div>
    <small v-if="isTouched && error" class="error-message">{{ error }}</small>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/input.pcss";

.root {
  width: 100%;
  display: flex;
  text-align: left;
  position: relative;
}

.root.is-switch {
  width: 100%;
  display: flex;
  text-align: left;
  position: relative;
  align-items: start;
  flex-direction: column;
}

.checkbox-wrapper,
.switch-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-control {
  position: relative;
  width: 1.25em;
  height: 1.25em;
  background-color: var(--main-background-color);
  border-radius: 0.5rem;
  box-shadow:
    0 0 0 1px var(--color-slate-200),
    0 2px 2px 0 var(--color-slate-100);
  transition: transform 0.3s;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-touched input:invalid + .checkbox-control {
  border: 1px solid var(--color-rose-600);
  box-shadow: none;
}

.control-label {
  @apply mp-font-sm;
  font-weight: 400;
  margin: 0 0 0 0.5rem;
  color: var(--color-slate-600);
  white-space: nowrap;
}

.meta-wrapper {
  display: flex;
  align-self: center;
}

.switch-control {
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s;
  width: 2.25rem;
  height: 1.25rem;
  background-color: var(--color-slate-100);
  border: 1px solid var(--color-slate-200);
  border-radius: 1rem;
  cursor: pointer;
}

.switch-thumb {
  width: 1rem;
  height: 1rem;
  background-color: var(--color-slate-300);
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: var(--shadow-base);
  margin-left: 0.1rem;
}

.switch-control.is-checked {
  background-color: var(--color-green-400);
  border-color: var(--color-green-500);
}

.switch-control.is-checked .switch-thumb {
  transform: translateX(1rem);
  background-color: var(--main-background-color);
}

.visually-hidden {
  position: absolute !important;
  margin-left: 0.25rem;
  height: 0.75rem;
  width: 0.75rem;
  overflow: hidden;
  white-space: nowrap; /* Prevent BFC creation */
}

.visually-hidden:focus-visible + .checkbox-control,
.visually-hidden:focus-visible + .switch-control {
  outline: 2px solid var(--color-indigo-400); /* Use your app's focus color */
  outline-offset: 2px;
}

.checkbox-wrapper,
.switch-wrapper {
  display: flex;
  align-items: center;
}

.checkbox-control.is-disabled,
.switch-control.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none; /* Prevents clicks on disabled state */
}

.helper-text {
  @apply mp-font-sm;
  & :deep(a) {
    @apply mp-font-sm;
    color: var(--color-rose-400);
  }
  margin-left: 0.25rem;
}

.error-message {
  @mixin mp-error-text;
  display: block;
}
</style>
